import { authState } from '../lib/auth.js';
import { getBackendUrl } from '../lib/backend.js';
import { BaseComponent } from '../lib/base-component.js';
import {
    PROVIDER_DISCORD,
    providerLabel,
    providerStartUrl,
    tenantSlugFromPath,
} from '../lib/providers.js';
import { router } from '../lib/router.js';
import { css, html } from '../vendor/lit.min.js';

const SSO_DOMAINS = ['dicedriven.com'];

// Shared sign-in form: email → (tenant select) → code, with SSO detection.
// Renders just the form (the host supplies the surrounding card/heading).
// On a successful local (email-code) sign-in it sets the global auth state,
// calls authState.notify(), and dispatches a composed `authenticated` event
// so the host can decide what to do next (navigate, generate an auth code,
// load device info, …). New users are sent to `/setup`. SSO sign-in triggers
// a full-page Entra redirect — the host is responsible for persisting any
// resume context (e.g. to sessionStorage) before the form is used.
class LoginForm extends BaseComponent {
    static properties = {
        email: { type: String, state: true },
        code: { type: String, state: true },
        error: { type: String, state: true },
        loading: { type: Boolean, state: true },
        step: { type: String, state: true },
        codeSent: { type: Boolean, state: true },
        _tenants: { type: Array, state: true },
        _selectedTenantId: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .code-input { font-size: 1.5rem; text-align: center; letter-spacing: 0.5rem; }

        /* Discord Blurple. Providers are recognised by their colour more than
           their name, so the button wears the brand rather than our own. */
        .btn-discord {
            background-color: #5865f2;
            border-color: #5865f2;
            color: #fff;
        }
        .btn-discord:hover,
        .btn-discord:focus {
            background-color: #4752c4;
            border-color: #4752c4;
            color: #fff;
        }
        .btn-discord:focus-visible {
            box-shadow: 0 0 0 0.25rem rgba(88, 101, 242, 0.5);
        }
        .btn-discord:disabled {
            background-color: #5865f2;
            border-color: #5865f2;
            color: #fff;
            opacity: 0.65;
        }
    `;

    constructor() {
        super();
        this.email = '';
        this.code = '';
        this.error = '';
        this.loading = false;
        this.step = 'email';
        this.codeSent = false;
        this._tenants = [];
        this._selectedTenantId = '';
    }

    _getEmailDomain() {
        const parts = this.email.split('@');
        return parts.length === 2 ? parts[1].toLowerCase() : '';
    }

    _isSSO() {
        return SSO_DOMAINS.includes(this._getEmailDomain());
    }

    async _handleEmailSubmit(e) {
        e.preventDefault();
        this.error = '';

        if (this._isSSO()) {
            this.loading = true;
            try {
                await authState.loginWithEntra(this.email);
            } catch (err) {
                this.error = err.message;
                this.loading = false;
            }
            return;
        }

        // Send login code
        this.loading = true;
        try {
            const body = { email: this.email };
            if (this._selectedTenantId) {
                body.tenant_id = this._selectedTenantId;
            }

            const response = await fetch(`${getBackendUrl()}/api/v1/send-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to send code');
            }

            // Remember the tenant the code was issued for. When the email
            // matched exactly one tenant the server chose it without showing a
            // chooser, so this is the only place the client learns it.
            if (data.tenant_id) {
                this._selectedTenantId = data.tenant_id;
            }

            // Multi-tenant: user needs to pick a tenant
            if (data.tenant_select_required) {
                this._tenants = data.tenants;
                this.step = 'tenant';
                return;
            }

            this.step = 'code';
            this.codeSent = true;
        } catch (err) {
            this.error = err.message;
        } finally {
            this.loading = false;
        }
    }

    _handleBack() {
        if (this.step === 'code' && this._tenants.length > 0) {
            this.step = 'tenant';
        } else {
            this.step = 'email';
            this._tenants = [];
            this._selectedTenantId = '';
        }
        this.code = '';
        this.error = '';
        this.codeSent = false;
    }

    async _handleTenantSelect(tenantId) {
        this._selectedTenantId = tenantId;
        this.error = '';
        // Re-submit send-code with the selected tenant
        await this._handleEmailSubmit(new Event('submit'));
    }

    async _handleCodeSubmit(e) {
        e.preventDefault();
        this.error = '';
        this.loading = true;

        try {
            const response = await fetch(`${getBackendUrl()}/api/v1/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // The tenant the code was issued for: without it the API falls
                // back to the D3 tenant, which is wrong for anyone who picked a
                // different one at the tenant step.
                body: JSON.stringify({
                    email: this.email,
                    code: this.code,
                    tenant_id: this._selectedTenantId,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Invalid code');
            }

            if (data.setup_required) {
                // New user - needs to pick a username. Carry the setup token
                // (proof of email ownership) into the setup step.
                sessionStorage.setItem('d3auth_setup_email', data.email);
                sessionStorage.setItem('d3auth_setup_token', data.setup_token);
                router.navigate('/setup');
                return;
            }

            authState.setTokens(data.tokens);
            await authState.fetchCurrentUser();
            authState.isAuthenticated = true;
            authState.notify();
            this.dispatchEvent(new CustomEvent('authenticated', { bubbles: true, composed: true }));
        } catch (err) {
            this.error = err.message;
        } finally {
            this.loading = false;
        }
    }

    async _handleResend() {
        this.error = '';
        this.loading = true;

        try {
            await fetch(`${getBackendUrl()}/api/v1/send-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // Carry the already-chosen tenant, or resending after the tenant
                // step would re-run multi-tenant resolution and could issue the
                // new code against a different tenant than the one on screen.
                body: JSON.stringify({ email: this.email, tenant_id: this._selectedTenantId }),
            });
            this.error = '';
            this.codeSent = true;
        } catch {
            this.error = 'Failed to resend code';
        } finally {
            this.loading = false;
        }
    }

    render() {
        return html`
            ${this.error ? html`<div class="alert alert-danger d-flex align-items-start gap-2"><i class="bi bi-exclamation-triangle-fill" style="font-size: 1.25rem; margin-top: 0.1rem;"></i> <span>${this.error}</span></div>` : ''}
            ${this.step === 'email' ? this._renderEmailStep() : this.step === 'tenant' ? this._renderTenantStep() : this._renderCodeStep()}
        `;
    }

    _renderTenantStep() {
        return html`
            <div class="mb-3">
                <div class="d-flex align-items-center">
                    <button type="button" class="btn btn-link p-0 me-2 text-decoration-none" @click="${this._handleBack}">
                        <i class="bi bi-arrow-left"></i>
                    </button>
                    <span class="text-muted">${this.email}</span>
                </div>
            </div>
            <p class="text-muted mb-3">This email is associated with multiple organizations. Please select one:</p>
            <div class="list-group">
                ${this._tenants.map(
                    (t) => html`
                    <button type="button" class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                        @click="${() => this._handleTenantSelect(t.id)}"
                        ?disabled="${this.loading}">
                        <span>${t.name}</span>
                        <i class="bi bi-chevron-right text-muted"></i>
                    </button>
                `,
                )}
            </div>
        `;
    }

    _renderEmailStep() {
        return html`
            <form @submit="${this._handleEmailSubmit}">
                <div class="mb-3">
                    <label for="email" class="form-label">Email</label>
                    <input type="email" class="form-control" id="email" required autofocus
                        .value="${this.email}"
                        @input="${(e) => (this.email = e.target.value)}">
                </div>
                <button type="submit" class="btn btn-primary w-100" ?disabled="${this.loading}">
                    ${this.loading ? 'Redirecting...' : 'Continue'}
                </button>
            </form>
            ${this._renderProviders()}
        `;
    }

    // Provider buttons appear only for providers this deployment configured, so
    // an environment without a Discord app doesn't offer a button that fails.
    _renderProviders() {
        const providers = authState.getEnabledProviders();
        if (!providers.includes(PROVIDER_DISCORD)) return '';

        return html`
            <div class="d-flex align-items-center gap-2 my-3">
                <hr class="flex-grow-1 m-0">
                <span class="text-muted small">or</span>
                <hr class="flex-grow-1 m-0">
            </div>
            <button type="button" class="btn btn-discord w-100 d-flex align-items-center justify-content-center gap-2"
                ?disabled="${this.loading}"
                @click="${this._handleDiscord}">
                <i class="bi bi-discord"></i>
                Sign in with ${providerLabel(PROVIDER_DISCORD)}
            </button>
        `;
    }

    _handleDiscord() {
        // A full-page navigation: the provider round trip has to happen in the
        // browser, not in a fetch. The pending-OAuth bridge in sessionStorage
        // survives it, so an app-initiated sign-in still resumes on return.
        const tenantSlug = tenantSlugFromPath(window.location.pathname);
        window.location.assign(providerStartUrl(PROVIDER_DISCORD, tenantSlug));
    }

    _renderCodeStep() {
        return html`
            <form @submit="${this._handleCodeSubmit}">
                <div class="mb-3">
                    <div class="d-flex align-items-center">
                        <button type="button" class="btn btn-link p-0 me-2 text-decoration-none" @click="${this._handleBack}">
                            <i class="bi bi-arrow-left"></i>
                        </button>
                        <span class="text-muted">${this.email}</span>
                    </div>
                </div>
                ${
                    this.codeSent
                        ? html`
                    <div class="alert alert-info">A sign-in code has been sent to your email.</div>
                `
                        : ''
                }
                <div class="mb-3">
                    <label for="code" class="form-label">Enter code</label>
                    <input type="text" class="form-control code-input" id="code" required autofocus
                        maxlength="6" inputmode="numeric" pattern="[0-9]{6}" autocomplete="one-time-code"
                        .value="${this.code}"
                        @input="${(e) => (this.code = e.target.value.replace(/\D/g, ''))}">
                </div>
                <button type="submit" class="btn btn-primary w-100" ?disabled="${this.loading || this.code.length !== 6}">
                    ${this.loading ? 'Verifying...' : 'Sign In'}
                </button>
                <div class="text-center mt-3">
                    <button type="button" class="btn btn-link text-decoration-none p-0" @click="${this._handleResend}" ?disabled="${this.loading}">
                        Resend code
                    </button>
                </div>
            </form>
        `;
    }
}

customElements.define('login-form', LoginForm);
export { LoginForm };
